import { queryOptions } from '@tanstack/react-query';
import { loadMeta, loadLight, loadFull, loadTopo, loadDistrictsMeta } from './loaders';
import { buildFullIndex, buildLightIndex } from './normalize';
import { activeMapCountry } from './active-country';
import type { TopoLevel } from './types';

// ─── Query key factory ───────────────────────────────────────────────────────
//
// Код активной страны входит в КАЖДЫЙ ключ: иначе React Query отдаст закешированные
// данные Италии, когда открыта другая страна. Ключи собираются в момент вызова,
// поэтому activeMapCountry() читается лениво и всегда актуален.

export const queryKeys = {
  meta: () => ['meta', activeMapCountry()] as const,
  light: (version: string) => ['units', activeMapCountry(), 'light', version] as const,
  full: (version: string) => ['units', activeMapCountry(), 'full', version] as const,
  topo: (level: TopoLevel, version: string) =>
    ['topo', activeMapCountry(), level, version] as const,
  lightIndex: (version: string) =>
    ['units', activeMapCountry(), 'light', version, 'index'] as const,
  fullIndex: (version: string) =>
    ['units', activeMapCountry(), 'full', version, 'index'] as const,
  districtsMeta: (version: string) =>
    ['districts', activeMapCountry(), 'meta', version] as const,
};

// ─── Query options ────────────────────────────────────────────────────────────

export const queries = {
  meta: () =>
    queryOptions({
      queryKey: queryKeys.meta(),
      queryFn: loadMeta,
      staleTime: 5 * 60 * 1000,
    }),

  light: (version: string) =>
    queryOptions({
      queryKey: queryKeys.light(version),
      queryFn: () => loadLight(version),
      staleTime: Infinity,
      gcTime: Infinity,
    }),

  full: (version: string) =>
    queryOptions({
      queryKey: queryKeys.full(version),
      queryFn: () => loadFull(version),
      staleTime: Infinity,
      gcTime: Infinity,
    }),

  topo: (level: TopoLevel, version: string) =>
    queryOptions({
      queryKey: queryKeys.topo(level, version),
      queryFn: () => loadTopo(level, version),
      staleTime: Infinity,
      gcTime: Infinity,
    }),

  lightIndex: (version: string) =>
    queryOptions({
      queryKey: queryKeys.lightIndex(version),
      queryFn: async () => {
        const comuni = await loadLight(version);
        return buildLightIndex(comuni);
      },
      staleTime: Infinity,
      gcTime: Infinity,
    }),

  fullIndex: (version: string) =>
    queryOptions({
      queryKey: queryKeys.fullIndex(version),
      queryFn: async () => {
        const comuni = await loadFull(version);
        return buildFullIndex(comuni);
      },
      staleTime: Infinity,
      gcTime: Infinity,
    }),

  districtsMeta: (version: string) =>
    queryOptions({
      queryKey: queryKeys.districtsMeta(version),
      queryFn: () => loadDistrictsMeta(version),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
};

// ─── Convenience hook helpers ─────────────────────────────────────────────────
// Usage pattern:
//   const { data: meta } = useQuery(queries.meta())
//   const { data: comuni } = useQuery(queries.full(meta!.version), { enabled: !!meta })
